/**
 * NegociosListView -- List of businesses (Nexo Digital clients) with inline create.
 *
 * @module presentation/views/NegociosListView
 */

import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { leerCatalogo, MENSAJE_SIN_CATALOGO } from '../../application/negocios/catalogo.js';
import { ordenarNegocios } from '../../application/negocios/ordenNegocios.js';
import { VISTAS, filtrarPorVista, conteoPorVista, insigniaDeEstado, listaDe } from '../../application/negocios/archivoNegocios.js';
import { insigniaHtml, COLOR_ESTADO } from '../shared/insignia.js';
import { SubscriptionApiClient } from '../../infrastructure/api/SubscriptionApiClient.js';
import { problemaDelAltaRapida, cuerpoDelAltaRapida, cuerpoDeLaSuscripcion, hoyEnBogota } from '../../application/negocios/altaRapida.js';
import { regresoHtml } from '../shared/regreso.js';

const LICENSE_COLORS = {
  active: COLOR_ESTADO.ok,
  trial: COLOR_ESTADO.alerta,
  expired: COLOR_ESTADO.error,
  suspended: COLOR_ESTADO.apagado
};

const LICENSE_LABELS = {
  active: 'activa',
  trial: 'prueba',
  expired: 'expirada',
  suspended: 'suspendida'
};

function licenseKey(raw) {
  if (raw === true)  return 'active';
  if (raw === false) return 'expired';
  return String(raw ?? '').toLowerCase();
}

function licenseBadge(raw) {
  const key = licenseKey(raw);
  const color = LICENSE_COLORS[key] || COLOR_ESTADO.apagado;
  const label = LICENSE_LABELS[key] || 'sin licencia';
  return insigniaHtml(label, color);
}

// NEGOCIOS-ARCHIVAR-VISTA-T1: el estado del negocio, para no confundir un archivado con uno que trabaja.
function estadoBadge(b) {
  const { etiqueta, color } = insigniaDeEstado(b);
  return insigniaHtml(etiqueta, color, 'data-estado-negocio');
}

function businessRow(b) {
  const id = b.businessId || b.id;
  return `
    <a href="#/negocios/${id}" class="artifact-item business-row" data-id="${id}">
      <div class="task-info">
        <div class="artifact-item-title">${b.displayName || '(sin nombre)'}</div>
        <div class="artifact-item-meta">
          ${b.legalName || ''} &middot; ${b.plan || '—'}
        </div>
      </div>
      <div class="task-actions">
        ${estadoBadge(b)}
        ${licenseBadge(b.licenseStatus)}
      </div>
    </a>`;
}

function buildOptions(items, placeholder) {
  const opts = items.map(v => `<option value="${v}">${v}</option>`).join('');
  return `<option value="" disabled selected>${placeholder}</option>${opts}`;
}

export async function renderNegociosList(container) {
  vistaNegocios = 'activos';
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Negocios</h1>
        </div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
          <a href="#/negocios/nuevo" class="btn-primary btn-sm" title="Wizard completo (7 pasos)">&#128295; Crear completo (wizard)</a>
          <button class="btn-ghost btn-sm" id="btn-new-negocio" title="Nombre, tipo, plan y celular; el resto lo llena el cliente">+ Rapido</button>
        </div>
      </div>

      <div id="nx-negocio-form-area" hidden>
        <form id="nx-negocio-form" class="inline-form inline-form-col">
          <p data-alta-rapida-ayuda style="margin:0 0 4px;font-size:0.875rem;color:var(--text-secondary)">Alta rapida: Nexo pone lo minimo y crea la suscripcion de 1 mes. El resto lo completa el cliente en el cuestionario del enlace.</p>
          <div class="form-row">
            <input type="text" id="nx-neg-displayName" placeholder="Nombre comercial *" required class="form-input">
            <input type="text" id="nx-neg-legalName" placeholder="Razon social (opcional, la llena el cliente)" class="form-input">
          </div>
          <div class="form-row">
            <select id="nx-neg-businessType" class="form-input form-input-sm">
              <option value="" disabled selected>Cargando tipos...</option>
            </select>
            <select id="nx-neg-plan" class="form-input form-input-sm" required>
              <option value="" disabled selected>Cargando planes...</option>
            </select>
          </div>
          <div class="form-row">
            <input type="tel" id="nx-neg-ownerPhone" placeholder="Celular del propietario * (su WhatsApp)" required inputmode="tel" autocomplete="off" class="form-input">
          </div>
          <div class="form-row">
            <button type="submit" class="btn-primary btn-sm">Crear</button>
            <button type="button" class="btn-ghost btn-sm" id="btn-cancel-negocio">Cancelar</button>
          </div>
        </form>
        <p id="nx-negocio-form-error" class="auth-error" hidden></p>
      </div>

      <div id="nx-negocio-vistas" role="group" aria-label="Que negocios ver" style="display:flex;gap:6px;flex-wrap:wrap;margin:0 0 10px"></div>

      <div class="artifact-list" id="nx-negocio-list">
        <p class="empty-state">Cargando negocios...</p>
      </div>
    </section>`;

  setupFormHandlers();
  await Promise.all([loadNegocios(), loadConfig()]);
}

async function loadConfig() {
  const $type = document.getElementById('nx-neg-businessType');
  const $plan = document.getElementById('nx-neg-plan');
  if (!$type || !$plan) return;

  // TIPO-NEGOCIO-CANON-T1: la lista sale de la base. Si no se puede leer, se
  // dice, y NO se ofrece una lista escrita aqui (habia cuatro y no coincidian).
  const catalogo = leerCatalogo(await BusinessApiClient.getConfig());

  $type.innerHTML = buildOptions(catalogo.tipos, catalogo.tipos.length ? 'Tipo de negocio *' : 'Sin lista de tipos');
  $plan.innerHTML = buildOptions(catalogo.planes, catalogo.planes.length ? 'Plan *' : 'Sin lista de planes');
  if (catalogo.estado !== 'ok' && !document.querySelector('[data-catalogo-aviso]')) {
    $plan.closest('.form-row').insertAdjacentHTML('afterend',
      `<p data-catalogo-aviso class="auth-error">${MENSAJE_SIN_CATALOGO}</p>`);
  }
}

function setupFormHandlers() {
  const $btn = document.getElementById('btn-new-negocio');
  const $area = document.getElementById('nx-negocio-form-area');
  const $form = document.getElementById('nx-negocio-form');
  const $cancel = document.getElementById('btn-cancel-negocio');
  const $error = document.getElementById('nx-negocio-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-neg-displayName').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    // PANEL-WIZARD-MINIMO-T1 (#15542): el cascaron. Nombre, tipo, plan y celular; la
    // razon social vacia no se rellena; la suscripcion de 1 mes nace aqui como en el wizard.
    const datos = {
      displayName: document.getElementById('nx-neg-displayName').value,
      legalName: document.getElementById('nx-neg-legalName').value,
      businessType: document.getElementById('nx-neg-businessType').value,
      plan: document.getElementById('nx-neg-plan').value,
      ownerPhone: document.getElementById('nx-neg-ownerPhone').value
    };
    const problema = problemaDelAltaRapida(datos);
    if (problema) {
      $error.textContent = problema;
      $error.hidden = false;
      return;
    }

    const $submit = $form.querySelector('button[type="submit"]');
    if ($submit) { $submit.disabled = true; $submit.textContent = 'Creando...'; }
    const listo = () => { if ($submit) { $submit.disabled = false; $submit.textContent = 'Crear'; } };

    const result = await BusinessApiClient.create(cuerpoDelAltaRapida(datos));
    if (!result.ok) {
      listo();
      $error.textContent = typeof result.error === 'string' ? result.error : (result.error?.message || 'No se pudo crear el negocio');
      $error.hidden = false;
      return;
    }
    const creado = result.data?.data || result.data || {};
    const bizId = creado.businessId || creado.id || creado.business_id;
    if (!bizId) {
      listo();
      $error.textContent = 'El negocio se creo pero la API no devolvio su id: buscalo en la lista y crea la suscripcion en su ficha.';
      $error.hidden = false;
      await loadNegocios();
      return;
    }

    const sub = await SubscriptionApiClient.create(bizId, cuerpoDeLaSuscripcion(datos.plan, hoyEnBogota()));
    if (!sub.ok) {
      listo();
      const motivo = typeof sub.error === 'string' ? sub.error : (sub.error?.message || 'error de la API');
      $error.innerHTML = '';
      $error.append('Negocio creado, pero la suscripcion fallo (' + motivo + '). Sin ella no pasa la licencia en el Desktop: ');
      const $link = document.createElement('a');
      $link.href = '#/negocios/' + encodeURIComponent(bizId);
      $link.textContent = 'abre la ficha y creala en Suscripcion';
      $error.append($link);
      $error.hidden = false;
      await loadNegocios();
      return;
    }

    listo();
    $area.hidden = true;
    $form.reset();
    window.location.hash = '#/negocios/' + encodeURIComponent(bizId);
  };
}

async function loadNegocios() {
  const $list = document.getElementById('nx-negocio-list');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';

  // NEGOCIOS-ARCHIVAR-VISTA-T1: se piden TODOS y el panel filtra (sirve con la API de antes y con la que ya no trae
  // los archivados sin ?status). Por defecto, los que trabajan con Nexo (activos y pendientes).
  const result = await BusinessApiClient.listTodos();

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  todosLosNegocios = listaDe(result);
  pintarNegocios();
}

let todosLosNegocios = [];
let vistaNegocios = 'activos';

function pintarNegocios() {
  const $list = document.getElementById('nx-negocio-list');
  const $vistas = document.getElementById('nx-negocio-vistas');
  if (!$list) return;

  const conteo = conteoPorVista(todosLosNegocios);
  if ($vistas) {
    $vistas.innerHTML = Object.entries(VISTAS).map(([k, v]) => `
      <button type="button" class="${k === vistaNegocios ? 'btn-primary' : 'btn-ghost'} btn-sm" data-vista-negocios="${k}" aria-pressed="${k === vistaNegocios}">${v.etiqueta} (${conteo[k]})</button>`).join('');
    $vistas.querySelectorAll('[data-vista-negocios]').forEach((b) => b.addEventListener('click', () => {
      vistaNegocios = b.dataset.vistaNegocios;
      pintarNegocios();
    }));
  }

  const items = filtrarPorVista(todosLosNegocios, vistaNegocios);
  if (items.length === 0) {
    $list.innerHTML = `<p class="empty-state">${todosLosNegocios.length ? `No hay negocios en "${VISTAS[vistaNegocios].etiqueta}"` : 'No hay negocios registrados'}</p>`;
    return;
  }

  // NEGOCIOS-ORDEN-T1: el API no ordena; la lista sale alfabetica por nombre comercial.
  $list.innerHTML = ordenarNegocios(items).map(businessRow).join('');
}
